import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { providerGroupsService, ProviderGroup } from '../services/providerGroupsService';
import { poolMembersService, PoolMember } from '../services/poolMembersService';
import { UserRole } from '../types';

type Tab = 'groups' | 'members';

const ProviderGroupsPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [tab, setTab] = useState<Tab>('members');
  const [groups, setGroups] = useState<ProviderGroup[]>([]);
  const [members, setMembers] = useState<PoolMember[]>([]);
  const [loading, setLoading] = useState(true);

  // Group modal
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ProviderGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });

  // Member modal
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState<PoolMember | null>(null);
  const [memberForm, setMemberForm] = useState({
    fullName: '', phone: '', email: '', password: '', vehicleInfo: '', plate: '', notes: '', groupId: '' as string | null,
  });

  const canManage = user?.role === UserRole.ORG_ADMIN || user?.role === UserRole.MANAGER || user?.role === UserRole.PLATFORM_ADMIN;

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [g, m] = await Promise.all([
        providerGroupsService.getAll(),
        poolMembersService.getAll(),
      ]);
      setGroups(g);
      setMembers(m);
    } catch {
      toast.error('Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // --- Group CRUD ---
  const openGroupModal = (group?: ProviderGroup) => {
    setEditingGroup(group || null);
    setGroupForm({ name: group?.name || '', description: group?.description || '' });
    setShowGroupModal(true);
  };

  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await providerGroupsService.update(editingGroup.id, groupForm);
        toast.success('Grup güncellendi');
      } else {
        await providerGroupsService.create(groupForm);
        toast.success('Grup oluşturuldu');
      }
      setShowGroupModal(false);
      loadData();
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleGroupDelete = async (id: string) => {
    if (!window.confirm('Bu grubu silmek istediğinizden emin misiniz?')) return;
    try {
      await providerGroupsService.delete(id);
      toast.success('Grup silindi');
      loadData();
    } catch { toast.error('Silinemedi'); }
  };

  // --- Member CRUD ---
  const openMemberModal = (member?: PoolMember) => {
    setEditingMember(member || null);
    setMemberForm({
      fullName: member?.fullName || '',
      phone: member?.phone || '',
      email: member?.email || '',
      password: '',
      vehicleInfo: member?.vehicleInfo || '',
      plate: member?.plate || '',
      notes: member?.notes || '',
      groupId: member?.groupId || null,
    });
    setShowMemberModal(true);
  };

  const handleMemberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = { ...memberForm, groupId: memberForm.groupId || null, password: memberForm.password || undefined };
      if (editingMember) {
        await poolMembersService.update(editingMember.id, data);
        toast.success('Üye güncellendi');
      } else {
        await poolMembersService.create(data);
        toast.success('Üye eklendi');
      }
      setShowMemberModal(false);
      loadData();
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleMemberDelete = async (id: string) => {
    if (!window.confirm('Bu üyeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await poolMembersService.delete(id);
      toast.success('Üye silindi');
      loadData();
    } catch { toast.error('Silinemedi'); }
  };

  const getGroupName = (groupId: string | null) => {
    if (!groupId) return '🌐 Genel Havuz';
    return groups.find(g => g.id === groupId)?.name || '-';
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><p className="text-gray-500">Yükleniyor...</p></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Havuz Yönetimi</h1>
        <p className="text-sm text-gray-500 mt-1">Havuz üyeleri ve grupları yönetin</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setTab('members')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'members' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          👤 Havuz Üyeleri ({members.length})
        </button>
        <button
          onClick={() => setTab('groups')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'groups' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          🏊 Gruplar ({groups.length})
        </button>
      </div>

      {/* MEMBERS TAB */}
      {tab === 'members' && (
        <div>
          {canManage && (
            <button onClick={() => openMemberModal()} className="mb-4 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium min-h-[44px]">
              + Havuz Üyesi Ekle
            </button>
          )}

          {members.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-4xl mb-3">👤</p>
              <p className="text-gray-500">Henüz havuz üyesi yok</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              {/* Mobile */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {members.map(m => (
                  <div key={m.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{m.fullName}</p>
                        <p className="text-sm text-gray-500">{m.phone}</p>
                        {m.vehicleInfo && <p className="text-sm text-gray-500">{m.vehicleInfo} {m.plate && `· ${m.plate}`}</p>}
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full">{getGroupName(m.groupId)}</span>
                      </div>
                      {canManage && (
                        <div className="flex gap-2">
                          <button onClick={() => openMemberModal(m)} className="text-blue-600 text-sm font-medium">Düzenle</button>
                          <button onClick={() => handleMemberDelete(m.id)} className="text-red-600 text-sm font-medium">Sil</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop */}
              <table className="hidden md:table min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ad Soyad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Araç</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grup</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {members.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{m.fullName}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{m.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{m.phone}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{m.vehicleInfo || '-'} {m.plate && `(${m.plate})`}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">{getGroupName(m.groupId)}</span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        {canManage && <>
                          <button onClick={() => openMemberModal(m)} className="text-blue-600 hover:text-blue-900 mr-3">Düzenle</button>
                          <button onClick={() => handleMemberDelete(m.id)} className="text-red-600 hover:text-red-900">Sil</button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* GROUPS TAB */}
      {tab === 'groups' && (
        <div>
          {canManage && (
            <button onClick={() => openGroupModal()} className="mb-4 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium min-h-[44px]">
              + Yeni Grup
            </button>
          )}

          {/* Genel Havuz kartı */}
          <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-blue-800 dark:text-blue-300">🌐 Genel Havuz</h3>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-0.5">
                  {members.filter(m => !m.groupId).length} üye · Tüm havuz üyeleri burada
                </p>
              </div>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Varsayılan</span>
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500">Henüz özel grup yok</p>
              <p className="text-sm text-gray-400 mt-1">Lüks Araç, Antalya gibi gruplar oluşturun</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {groups.map(g => {
                const memberCount = members.filter(m => m.groupId === g.id).length;
                return (
                  <div key={g.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{g.name}</h3>
                        {g.description && <p className="text-sm text-gray-500">{g.description}</p>}
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${g.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {g.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{memberCount} üye</p>
                    {canManage && (
                      <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <button onClick={() => openGroupModal(g)} className="flex-1 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium">Düzenle</button>
                        <button onClick={() => handleGroupDelete(g.id)} className="flex-1 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded font-medium">Sil</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* GROUP MODAL */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{editingGroup ? 'Grubu Düzenle' : 'Yeni Grup'}</h2>
              <button onClick={() => setShowGroupModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleGroupSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grup Adı *</label>
                <input type="text" value={groupForm.name} onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="örn. Antalya Grubu, Lüks Araç" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama</label>
                <input type="text" value={groupForm.description} onChange={e => setGroupForm({ ...groupForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="İsteğe bağlı" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowGroupModal(false)} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 dark:text-gray-300">İptal</button>
                <button type="submit" className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">{editingGroup ? 'Güncelle' : 'Oluştur'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MEMBER MODAL */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{editingMember ? 'Üyeyi Düzenle' : 'Yeni Havuz Üyesi'}</h2>
              <button onClick={() => setShowMemberModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleMemberSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ad Soyad *</label>
                <input type="text" value={memberForm.fullName} onChange={e => setMemberForm({ ...memberForm, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Mehmet Yılmaz" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon *</label>
                <input type="tel" value={memberForm.phone} onChange={e => setMemberForm({ ...memberForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="+90 555 123 4567" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                <input type="email" value={memberForm.email} onChange={e => setMemberForm({ ...memberForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="ornek@email.com" required={!editingMember} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {editingMember ? 'Yeni Şifre (boş bırakılırsa değişmez)' : 'Şifre *'}
                </label>
                <input type="password" value={memberForm.password} onChange={e => setMemberForm({ ...memberForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="••••••••" required={!editingMember} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Araç Bilgisi</label>
                <input type="text" value={memberForm.vehicleInfo} onChange={e => setMemberForm({ ...memberForm, vehicleInfo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="Mercedes E200, 7 kişilik minibüs..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plaka</label>
                <input type="text" value={memberForm.plate} onChange={e => setMemberForm({ ...memberForm, plate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  placeholder="34 ABC 123" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grup</label>
                <select value={memberForm.groupId || ''} onChange={e => setMemberForm({ ...memberForm, groupId: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                  <option value="">🌐 Genel Havuz</option>
                  {groups.filter(g => g.isActive).map(g => (
                    <option key={g.id} value={g.id}>🏊 {g.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notlar</label>
                <textarea value={memberForm.notes} onChange={e => setMemberForm({ ...memberForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                  rows={2} placeholder="İsteğe bağlı" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowMemberModal(false)} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 dark:text-gray-300">İptal</button>
                <button type="submit" className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">{editingMember ? 'Güncelle' : 'Ekle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderGroupsPage;
